import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Mail, Bell, CheckCircle, XCircle, Clock, Send, Building2, Search, X } from 'lucide-react';
import { authFetch } from '../services/api';
import useRecetas from '../hooks/usePuedeEmitirRecetas';
import RescheduleModal from './RescheduleModal';
import DayTimelineView, { cierreDelDia } from './DayTimelineView';
import AppointmentNoteEditor from './AppointmentNoteEditor';
import DetalleTurno from './DetalleTurno';
import type { QuickAddData, ServiceOption, SpecialDayInfo, OpenSpecialDayData, DiaCerradoInfo } from './DayTimelineView';
import MiniCalendarSlide from './MiniCalendarSlide';
import ConfirmModal from './ConfirmModal';
import './PublicProfessionalLanding.css';

// =====================================================
// INTERFACES
// =====================================================

interface NotificationLog {
  status: string;
  template_name: string;
  created_at: string;
  error_message: string | null;
}

interface Appointment {
  id: number;
  professional_id: number;
  client_name?: string;
  client_display_name: string;
  client_email?: string;
  client_phone?: string;
  service_name: string;
  service_category?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  price: number;
  deposit_required?: boolean;
  deposit_amount?: number;
  deposit_paid?: boolean;
  notes?: string;
  professional_notes?: string;
  is_first_time?: boolean;
  is_rescheduled?: boolean;
  rescheduled_from?: string;
  urgency_level?: 'low' | 'normal' | 'high';
  created_at?: string;
  updated_at?: string;
  location_id?: number;
  location_name?: string;
  location_city?: string;
  metodo_pago_elegido?: string;
  pago_verificado?: boolean;
  // Notification tracking
  reminder_sent?: boolean;
  reminder_sent_at?: string;
  whatsapp_confirmation?: NotificationLog | null;
  whatsapp_reminder?: NotificationLog | null;
  email_confirmation?: NotificationLog | null;
  email_reminder?: NotificationLog | null;
  patient_extra_data?: Record<string, any>;
}

// Helper: parsear fecha como local (evita desfase UTC)
const parseLocalDate = (dateStr: string): Date => {
  const parts = dateStr.split('T')[0].split('-');
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12);
};

// Helper: mostrar nombre multi-servicio si existe
const getServiceDisplay = (apt: Appointment): string => {
  try {
    const extra = apt.patient_extra_data;
    if (extra && extra.all_service_names) return extra.all_service_names;
  } catch (e) {}
  return apt.service_name || 'Consulta';
};

interface WorkSchedule {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_start?: string;
  break_end?: string;
  is_working: boolean;
}

interface User {
  id: number;
  nombre: string;
  apellido: string;
  business_name?: string;
  professional_type?: 'salud' | 'belleza';
}

interface CalendarAgendaViewProps {
  appointments: any[]; // Acepta CalendarAppointment de AdvancedCalendar
  schedules: WorkSchedule[];
  user: User;
  onAppointmentClick: (appointment: any) => void;
  onAppointmentAction: (appointmentId: number, action: string, data?: any) => Promise<void>;
  onRefresh: () => void;
  loading?: boolean;
  currentDate?: Date;
  onDateChange?: (date: Date) => void;
}

// =====================================================
// UTILIDADES
// =====================================================

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  }).format(price);
};

// =====================================================
// COMPONENTE DE INDICADORES DE NOTIFICACIÓN
// =====================================================
const NotificationIndicators: React.FC<{ appointment: any; compact?: boolean }> = ({ appointment: apt, compact = false }) => {
  if (compact) {
    return (
      <div className="flex items-center gap-1 mt-1">
        {apt.whatsapp_confirmation?.status === 'sent' && <MessageCircle className="w-3 h-3 text-green-500" />}
        {apt.whatsapp_confirmation && apt.whatsapp_confirmation.status !== 'sent' && <MessageCircle className="w-3 h-3 text-red-400" />}
        {(apt.whatsapp_reminder?.status === 'sent' || apt.reminder_sent) && <Bell className="w-3 h-3 text-blue-500" />}
        {apt.whatsapp_reminder && apt.whatsapp_reminder.status !== 'sent' && <Bell className="w-3 h-3 text-red-400" />}
        {apt.email_confirmation?.status === 'sent' && <Mail className="w-3 h-3 text-emerald-500" />}
        {apt.email_confirmation && apt.email_confirmation.status !== 'sent' && <Mail className="w-3 h-3 text-red-400" />}
        {apt.email_reminder?.status === 'sent' && <Send className="w-3 h-3 text-teal-500" />}
        {apt.email_reminder && apt.email_reminder.status !== 'sent' && <Send className="w-3 h-3 text-red-400" />}
      </div>
    );
  }

  return (
    <div className="space-y-1.5 mt-3">
      {apt.whatsapp_confirmation ? (
        apt.whatsapp_confirmation.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-green-600"
            title={`Enviado: ${new Date(apt.whatsapp_confirmation.created_at).toLocaleString('es-AR')}`}>
            <MessageCircle className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>WA confirmación enviada</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500"
            title={apt.whatsapp_confirmation.error_message || 'Error'}>
            <MessageCircle className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error WA confirmación</span>
          </div>
        )
      ) : apt.client_phone ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <MessageCircle className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Sin confirmación WA</span>
        </div>
      ) : null}

      {apt.whatsapp_reminder ? (
        apt.whatsapp_reminder.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-blue-600"
            title={`Enviado: ${new Date(apt.whatsapp_reminder.created_at).toLocaleString('es-AR')}`}>
            <Bell className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Recordatorio WA enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <Bell className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error recordatorio WA</span>
          </div>
        )
      ) : apt.reminder_sent ? (
        <div className="flex items-center gap-1.5 text-xs text-blue-600">
          <Bell className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Recordatorio enviado</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Bell className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Recordatorio pendiente</span>
        </div>
      )}

      {apt.email_confirmation ? (
        apt.email_confirmation.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600"
            title={`Enviado: ${new Date(apt.email_confirmation.created_at).toLocaleString('es-AR')}`}>
            <Mail className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Email confirmación enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <Mail className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error email confirmación</span>
          </div>
        )
      ) : apt.client_email ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Mail className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Sin email confirmación</span>
        </div>
      ) : null}

      {apt.email_reminder ? (
        apt.email_reminder.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-teal-600"
            title={`Enviado: ${new Date(apt.email_reminder.created_at).toLocaleString('es-AR')}`}>
            <Send className="w-3.5 h-3.5" /><CheckCircle className="w-3 h-3" /><span>Email recordatorio enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500">
            <Send className="w-3.5 h-3.5" /><XCircle className="w-3 h-3" /><span>Error email recordatorio</span>
          </div>
        )
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Send className="w-3.5 h-3.5" /><Clock className="w-3 h-3" /><span>Email rec. pendiente</span>
        </div>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const CalendarAgendaView: React.FC<CalendarAgendaViewProps> = ({
  appointments,
  schedules,
  user,
  onAppointmentClick,
  onAppointmentAction,
  onRefresh,
  loading = false,
  currentDate,
  onDateChange
}) => {
  // Estados
  const [internalSelectedDate, setInternalSelectedDate] = useState(new Date());
  const selectedDate = currentDate || internalSelectedDate;
  
  const setSelectedDate = (d: Date) => {
    if (onDateChange) onDateChange(d);
    setInternalSelectedDate(d);
  };
  
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmHideOpen, setConfirmHideOpen] = useState(false); // "Borrar" cancelado
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  // Sobreturnos
  const [maxOverridesPerSlot, setMaxOverridesPerSlot] = useState(0);
  // Servicios para QuickAdd
  const [agendaServices, setAgendaServices] = useState<ServiceOption[]>([]);
  // Bloqueo de horarios
  const [blockedHours, setBlockedHours] = useState<{ blockedFrom: string; blockedUntil?: string; reason?: string } | null>(null);
  // Fechas con horarios bloqueados (para indicador en mini calendario)
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  // Día especial de la fecha elegida (abrir un día que no se atiende)
  const [specialDay, setSpecialDay] = useState<SpecialDayInfo | null>(null);
  const [diaCerrado, setDiaCerrado] = useState<DiaCerradoInfo | null>(null);
  const [recargarEspecial, setRecargarEspecial] = useState(0);
  const [sedes, setSedes] = useState<{ id: number; name: string }[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [pendingTransferAppointment, setPendingTransferAppointment] = useState<Appointment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRemindersPanel, setShowRemindersPanel] = useState(false);
  const navigate = useNavigate();
  const { mostrar: mostrarBotonReceta, puedeEmitir: puedeEmitirRecetas } = useRecetas();

  // Obtener horario del día seleccionado
  const daySchedule = useMemo(() => {
    const dayOfWeek = selectedDate.getDay();
    return schedules.find(s => s.day_of_week === dayOfWeek) || null;
  }, [selectedDate, schedules]);

  // Obtener días laborales
  const workingDays = useMemo(() => {
    return schedules.filter(s => s.is_working).map(s => s.day_of_week);
  }, [schedules]);

  // Obtener fechas con turnos
  const appointmentDates = useMemo(() => {
    const dates: string[] = [];
    appointments
      .filter(apt => apt.status !== 'cancelled')
      .forEach(apt => {
        const dateStr = apt.appointment_date.split('T')[0];
        if (!dates.includes(dateStr)) {
          dates.push(dateStr);
        }
      });
    return dates;
  }, [appointments]);

  // Nombre del profesional
  const professionalName = user.business_name || `${user.nombre} ${user.apellido}`;

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Cargar configuración de sobreturnos
  useEffect(() => {
    const loadOverrideConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/schedule/professional/${user.id}/config/public`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.scheduleConfig) {
            setMaxOverridesPerSlot(data.scheduleConfig.maxOverridesPerSlot || 0);
          }
        }
      } catch (err) { console.error('Error cargando config sobreturnos:', err); }
    };
    loadOverrideConfig();
  }, [user.id, API_URL]);

  // Cargar servicios del profesional para QuickAdd
  useEffect(() => {
    const loadServices = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/services/professional/${user.id}/services`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.services) {
            setAgendaServices(data.services.map((s: any) => ({
              id: s.id, name: s.name, duration: s.duration, price: s.price
            })));
          }
        }
      } catch (err) { console.error('Error cargando servicios:', err); }
    };
    loadServices();
  }, [user.id, API_URL]);

  // ✅ Helper: convertir Date a YYYY-MM-DD usando timezone local (no UTC)
  // toISOString() convierte a UTC y puede saltar de día en zonas horarias negativas (Argentina)
  const dateToYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Helper extraer YYYY-MM-DD de un valor de exception_date (string o Date)
  const exceptionDateToYMD = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'string') return val.split('T')[0];
    try {
      const d = new Date(val);
      return dateToYMD(d);
    } catch {
      return '';
    }
  };

  // Cargar bloqueos del día seleccionado + lista global de fechas bloqueadas
  // Refetch automático cuando cambia selectedDate o el tab vuelve a estar visible
  useEffect(() => {
    let cancelled = false;
    const dateStr = dateToYMD(selectedDate);

    const loadDayBlock = async () => {
      try {
        // Pedido específico del día (alineado con MobileMonthCalendar)
        const response = await authFetch(
          `${API_URL}/api/schedule/professional/${user.id}/exceptions?date=${dateStr}`
        );
        if (!response.ok) { setDiaCerrado(null); return; }
        const data = await response.json();
        if (cancelled) return;
        const exceptions = data.exceptions || data || [];
        setDiaCerrado(Array.isArray(exceptions) ? cierreDelDia(exceptions, dateStr) : null);
        const especial = exceptions.find(
          (ex: any) => exceptionDateToYMD(ex.exception_date) === dateStr
            && ex.exception_type === 'custom_hours' && ex.is_working
            && ex.custom_start_time && ex.custom_end_time
        );
        setSpecialDay(especial ? {
          id: especial.id,
          start: String(especial.custom_start_time).slice(0, 5),
          end: String(especial.custom_end_time).slice(0, 5),
          breakStart: especial.break_start ? String(especial.break_start).slice(0, 5) : null,
          breakEnd: especial.break_end ? String(especial.break_end).slice(0, 5) : null,
          reason: especial.reason || null,
        } : null);
        const dayException = exceptions.find(
          (ex: any) => exceptionDateToYMD(ex.exception_date) === dateStr
            && ex.exception_type === 'blocked_hours'
            && ex.custom_end_time
        );
        if (dayException) {
          setBlockedHours({
            blockedFrom: dayException.custom_end_time.substring(0, 5),
            reason: dayException.reason
          });
          return;
        }

        // Sin bloqueo "resto del día": buscar bloqueo por rango (time_blocks)
        const rangeResponse = await authFetch(
          `${API_URL}/api/schedule/professional/${user.id}/time-blocks?date=${dateStr}`
        );
        if (rangeResponse.ok) {
          const rangeData = await rangeResponse.json();
          const block = (rangeData.timeBlocks || [])[0];
          if (!cancelled && block) {
            setBlockedHours({
              blockedFrom: String(block.start_time).substring(0, 5),
              blockedUntil: String(block.end_time).substring(0, 5),
              reason: block.reason
            });
            return;
          }
        }
        if (!cancelled) setBlockedHours(null);
      } catch (error) {
        console.error('Error cargando bloqueo del día:', error);
        if (!cancelled) setBlockedHours(null);
      }
    };

    const loadAllBlocked = async () => {
      try {
        // Pedido sin filtro de fecha para armar la lista del mini-calendario
        const response = await authFetch(
          `${API_URL}/api/schedule/professional/${user.id}/exceptions`
        );
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const exceptions = data.exceptions || data || [];
        const allBlocked = exceptions
          .filter((ex: any) => ex.exception_type === 'blocked_hours' && ex.exception_date)
          .map((ex: any) => exceptionDateToYMD(ex.exception_date))
          .filter(Boolean);

        // Sumar fechas con bloqueos por rango (time_blocks)
        let rangeDates: string[] = [];
        try {
          const rangeResponse = await authFetch(
            `${API_URL}/api/schedule/professional/${user.id}/time-blocks`
          );
          if (rangeResponse.ok) {
            const rangeData = await rangeResponse.json();
            rangeDates = (rangeData.timeBlocks || [])
              .map((b: any) => exceptionDateToYMD(b.block_date))
              .filter(Boolean);
          }
        } catch { /* opcional: sin rangos no pasa nada */ }

        if (cancelled) return;
        setBlockedDates(Array.from(new Set([...allBlocked, ...rangeDates])));
      } catch (error) {
        console.error('Error cargando lista de bloqueos:', error);
      }
    };

    loadDayBlock();
    loadAllBlocked();

    // Refrescar cuando el tab vuelve a estar visible (cambios desde otro dispositivo)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadDayBlock();
        loadAllBlocked();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selectedDate, user.id, API_URL, recargarEspecial]);

  // Sedes activas, para elegir dónde se abre un día especial (si hay más de una)
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const r = await authFetch(`${API_URL}/api/locations/professional/${user.id}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelado) return;
        setSedes((data.locations || [])
          .filter((l: any) => l.is_active !== false)
          .map((l: any) => ({ id: l.id, name: l.name })));
      } catch { /* sin sedes: el día especial se guarda sin sede */ }
    })();
    return () => { cancelado = true; };
  }, [user.id, API_URL]);

  // Bloquear horarios (untilTime opcional: rango; sin untilTime: resto del día)
  const handleBlockHours = async (fromTime: string, untilTime?: string, reason?: string): Promise<boolean> => {
    try {
      const dateStr = dateToYMD(selectedDate);
      const response = await authFetch(
        `${API_URL}/api/schedule/professional/${user.id}/block-hours`,
        {
          method: 'POST',
          body: JSON.stringify({ date: dateStr, fromTime, untilTime, reason })
        }
      );
      if (response.ok) {
        setBlockedHours({ blockedFrom: fromTime, blockedUntil: untilTime, reason });
        setBlockedDates(prev => prev.includes(dateStr) ? prev : [...prev, dateStr]);
        onRefresh();
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error bloqueando horarios - Status:', response.status, errorData);
        alert(`Error al bloquear horarios: ${errorData.error || errorData.message || 'Error desconocido'}`);
        return false;
      }
    } catch (error) {
      console.error('Error bloqueando horarios:', error);
      alert('Error de conexión al bloquear horarios. Verificá tu conexión a internet.');
      return false;
    }
  };

  // Desbloquear horarios
  const handleUnblockHours = async (): Promise<boolean> => {
    try {
      const dateStr = dateToYMD(selectedDate);
      const response = await authFetch(
        `${API_URL}/api/schedule/professional/${user.id}/unblock-hours`,
        {
          method: 'POST',
          body: JSON.stringify({ date: dateStr })
        }
      );
      if (response.ok) {
        setBlockedHours(null);
        setBlockedDates(prev => prev.filter(d => d !== dateStr));
        onRefresh();
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error desbloqueando horarios - Status:', response.status, errorData);
        alert(`Error al desbloquear horarios: ${errorData.error || errorData.message || 'Error desconocido'}`);
        return false;
      }
    } catch (error) {
      console.error('Error desbloqueando horarios:', error);
      alert('Error de conexión al desbloquear horarios.');
      return false;
    }
  };

  // Abrir el día (día especial, igual al de Horarios > Días especiales). Lo usan
  // sobre todo las secretarias: el servidor ya se lo permitía, pero en su
  // panel no había dónde.
  const handleOpenSpecialDay = async (d: OpenSpecialDayData): Promise<boolean> => {
    try {
      const response = await authFetch(`${API_URL}/api/schedule/professional/${user.id}/exception`, {
        method: 'POST',
        body: JSON.stringify({
          exceptionDate: dateToYMD(selectedDate),
          exceptionType: 'custom_hours',
          isWorking: true,
          customStartTime: d.start,
          customEndTime: d.end,
          customBreakStart: d.breakStart || null,
          customBreakEnd: d.breakEnd || null,
          reason: d.reason || 'Día especial',
          locationId: d.locationId ?? null
        })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setRecargarEspecial((n) => n + 1);
        onRefresh();
        return true;
      }
      alert(`No se pudo abrir el día: ${data.message || data.error || 'error desconocido'}`);
      return false;
    } catch (error) {
      console.error('Error abriendo el día:', error);
      alert('Error de conexión al abrir el día.');
      return false;
    }
  };

  // Quitar el día especial
  const handleRemoveSpecialDay = async (): Promise<boolean> => {
    if (!specialDay?.id) return false;
    try {
      const response = await authFetch(
        `${API_URL}/api/schedule/professional/${user.id}/exception/${specialDay.id}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setSpecialDay(null);
        setRecargarEspecial((n) => n + 1);
        onRefresh();
        return true;
      }
      const data = await response.json().catch(() => ({}));
      alert(`No se pudo quitar el día especial: ${data.error || 'error desconocido'}`);
      return false;
    } catch (error) {
      console.error('Error quitando el día especial:', error);
      alert('Error de conexión al quitar el día especial.');
      return false;
    }
  };

  // Crear sobreturno / nuevo turno desde timeline
  const handleAddAppointment = (date: string, time: string) => {
    // Verificar si ya hay un turno a esa hora (sobreturno)
    const hasConflict = appointments.some(apt => {
      const aptDate = apt.appointment_date.split('T')[0];
      const aptTime = apt.appointment_time.substring(0, 5);
      return aptDate === date && aptTime === time && apt.status !== 'cancelled';
    });
    const params = new URLSearchParams({ date, time });
    if (hasConflict) params.set('override', 'true');
    navigate(`/professional/new-appointment?${params.toString()}`);
  };

  // QuickAdd: crear turno rápido desde timeline
  const handleQuickAdd = async (data: QuickAddData): Promise<boolean> => {
    try {
      const token = localStorage.getItem('token');
      // Detectar override: usar flag del QuickAdd o verificar en appointments
      const hasConflict = !appointments.every(apt => {
        const aptDate = apt.appointment_date.split('T')[0];
        const aptTime = apt.appointment_time.substring(0, 5);
        return !(aptDate === data.date && aptTime === data.time && apt.status !== 'cancelled');
      });

      const body: any = {
        professionalId: user.id,
        appointmentDate: data.date,
        appointmentTime: data.time,
        clientName: data.clientName,
        clientPhone: data.clientPhone || null,
        notes: data.notes || null,
        isOverride: data.isOverride || hasConflict
      };

      if (data.serviceId) {
        body.serviceId = data.serviceId;
      } else {
        body.customServiceName = data.serviceName;
        body.customDuration = data.duration;
      }

      const res = await authFetch(`${API_URL}/api/assistants/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const result = await res.json();
      if (result.success) {
        onRefresh();
        return true;
      }
      console.error('Error creando turno rápido:', result.error);
      return false;
    } catch (err) {
      console.error('Error en QuickAdd:', err);
      return false;
    }
  };

  // Navegación de fechas
  const goToToday = () => setSelectedDate(new Date());
  
  const goToPrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };
  
  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  // Manejar click en turno
  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setNotesValue(appointment.professional_notes || '');
    setShowDetailModal(true);
  };

  // Manejar WhatsApp
  const handleWhatsAppClick = (appointment: Appointment) => {
    if (!appointment.client_phone) return;
    
    const message = encodeURIComponent(
      `Hola ${appointment.client_display_name}, te contacto desde ${professionalName} sobre tu turno del ${parseLocalDate(appointment.appointment_date).toLocaleDateString('es-AR')} a las ${appointment.appointment_time.substring(0, 5)}.`
    );
    const phone = appointment.client_phone.replace(/\D/g, '');
    const formattedPhone = phone.startsWith('54') ? phone : `54${phone}`;
    window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
  };

  // Interceptar confirmación para turnos con transferencia pendiente
  const handleConfirmWithTransferCheck = () => {
    if (!selectedAppointment) return;
    if (
      selectedAppointment.status === 'pending' &&
      selectedAppointment.metodo_pago_elegido === 'transferencia' &&
      !selectedAppointment.pago_verificado
    ) {
      setPendingTransferAppointment(selectedAppointment);
      setShowTransferModal(true);
      return;
    }
    handleAction('confirmed');
  };

  // Acciones sobre turnos
  const handleAction = async (action: string, data?: any) => {
    if (!selectedAppointment) return;

    setActionLoading(true);
    try {
      await onAppointmentAction(selectedAppointment.id, action, data);
      setShowDetailModal(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error ejecutando acción:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // "Borrar" un turno cancelado: soft-hide reversible (no se borra, queda registro)
  const handleHideAppointment = async () => {
    if (!selectedAppointment) return;
    setActionLoading(true);
    try {
      await authFetch(`${API_URL}/api/appointments/appointment/${selectedAppointment.id}/hide`, { method: 'PATCH' });
      setConfirmHideOpen(false);
      setShowDetailModal(false);
      setSelectedAppointment(null);
      onRefresh();
    } catch (error) {
      console.error('Error ocultando turno:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Guardar notas
  const handleSaveNotes = async () => {
    if (!selectedAppointment) return;
    
    setActionLoading(true);
    try {
      await onAppointmentAction(selectedAppointment.id, selectedAppointment.status, {
        professionalNotes: notesValue
      });
      setEditingNotes(false);
    } catch (error) {
      console.error('Error guardando notas:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Formatear fecha para el header
  const formattedHeaderDate = selectedDate.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Filtrar turnos por búsqueda
  const searchFilteredAppointments = useMemo(() => {
    if (!searchTerm.trim()) return appointments;
    const term = searchTerm.toLowerCase();
    return appointments.filter(apt =>
      (apt.client_display_name || apt.client_name || '').toLowerCase().includes(term) ||
      (apt.service_name || '').toLowerCase().includes(term) ||
      (apt.client_phone || '').includes(term) ||
      (apt.client_email || '').toLowerCase().includes(term)
    );
  }, [appointments, searchTerm]);

  return (
    <div className="calendar-agenda-view min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Botón calendario (solo mobile) + Fecha */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowMiniCalendar(true)}
                className="lg:hidden flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200 active:scale-95"
                title="Abrir calendario"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>

              <div className="min-w-0">
                <h1 className="text-base lg:text-lg font-bold text-gray-900 capitalize truncate">
                  {formattedHeaderDate}
                </h1>
                <p className="text-xs lg:text-sm text-gray-500">
                  {specialDay
                    ? `Día especial · ${specialDay.start} - ${specialDay.end}`
                    : daySchedule?.is_working
                      ? `${daySchedule.start_time.substring(0, 5)} - ${daySchedule.end_time.substring(0, 5)}`
                      : 'No laborable'
                  }
                </p>
              </div>
            </div>

            {/* Barra de búsqueda (centro) */}
            <div className="hidden md:flex flex-1 max-w-sm mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Filtrar agenda..."
                  className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 bg-gray-50/80 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 focus:bg-white transition-all placeholder:text-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Recordatorios badge + Controles de navegación */}
            <div className="flex items-center space-x-1 lg:space-x-2">
              {/* Botón Recordatorios */}
              {(() => {
                const sd = selectedDate.toISOString().split('T')[0];
                const dayApts = appointments.filter((a: any) => a.appointment_date.split('T')[0] === sd && a.status !== 'cancelled');
                const withWa = dayApts.filter((a: any) => a.whatsapp_confirmation?.status === 'sent').length;
                const total = dayApts.length;
                const allSent = total > 0 && withWa === total;
                const someSent = withWa > 0 && withWa < total;
                return (
                  <button
                    onClick={() => setShowRemindersPanel(!showRemindersPanel)}
                    className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                      showRemindersPanel
                        ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title="Recordatorios"
                  >
                    <Bell className="w-4 h-4" />
                    <span className="hidden lg:inline">Recordatorios</span>
                    {total > 0 && (
                      <span className={`ml-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold ${
                        allSent ? 'bg-green-500 text-white' :
                        someSent ? 'bg-amber-500 text-white' :
                        'bg-gray-300 text-gray-600'
                      }`}>
                        {withWa}/{total}
                      </span>
                    )}
                  </button>
                );
              })()}

              <div className="w-px h-6 bg-gray-200 mx-1" />

              <button
                onClick={goToPrevDay}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={goToToday}
                className="px-3 lg:px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm transition-colors"
              >
                Hoy
              </button>

              <button
                onClick={goToNextDay}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1 lg:mx-2" />

              <button
                onClick={onRefresh}
                disabled={loading}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <svg className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* Barra de búsqueda mobile (debajo del header) */}
          <div className="md:hidden mt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filtrar agenda..."
                className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 bg-gray-50/80 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 focus:bg-white transition-all placeholder:text-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Panel de Recordatorios (dropdown desde header) */}
      {showRemindersPanel && (() => {
        const sd = selectedDate.toISOString().split('T')[0];
        const dayApts = appointments.filter((a: any) => a.appointment_date.split('T')[0] === sd && a.status !== 'cancelled');

        if (dayApts.length === 0) {
          return (
            <div className="max-w-7xl mx-auto px-4">
              <div className="bg-white rounded-b-2xl border border-t-0 border-gray-200 shadow-lg p-6 text-center" style={{ animation: 'scaleIn 0.2s ease-out' }}>
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No hay turnos para este día</p>
              </div>
            </div>
          );
        }

        const withWaConfirm = dayApts.filter((a: any) => a.whatsapp_confirmation?.status === 'sent').length;
        const withWaReminder = dayApts.filter((a: any) => a.whatsapp_reminder?.status === 'sent' || a.reminder_sent).length;
        const withEmailConfirm = dayApts.filter((a: any) => a.email_confirmation?.status === 'sent').length;

        return (
          <div className="max-w-7xl mx-auto px-4">
            <div className="bg-white rounded-b-2xl border border-t-0 border-gray-200 shadow-lg p-4" style={{ animation: 'scaleIn 0.2s ease-out' }}>
              {/* Resumen rápido */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                  <MessageCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-700">{withWaConfirm}/{dayApts.length}</p>
                  <p className="text-[10px] text-green-600 font-medium uppercase tracking-wide">WA Confirm.</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                  <Bell className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-blue-700">{withWaReminder}/{dayApts.length}</p>
                  <p className="text-[10px] text-blue-600 font-medium uppercase tracking-wide">WA Recorda.</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                  <Mail className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-emerald-700">{withEmailConfirm}/{dayApts.length}</p>
                  <p className="text-[10px] text-emerald-600 font-medium uppercase tracking-wide">Email Conf.</p>
                </div>
              </div>

              {/* Lista detallada de pacientes */}
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {dayApts.sort((a: any, b: any) => a.appointment_time.localeCompare(b.appointment_time)).map((apt: any) => (
                  <div key={apt.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-xs font-mono text-gray-500 flex-shrink-0">
                        {apt.appointment_time?.substring(0, 5)}
                      </span>
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {apt.client_display_name || apt.client_name || 'Sin nombre'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      {/* WA Confirmación */}
                      <span title={apt.whatsapp_confirmation?.status === 'sent' ? 'WA confirmación enviada' : 'WA confirmación pendiente'}>
                        <MessageCircle className={`w-4 h-4 ${
                          apt.whatsapp_confirmation?.status === 'sent' ? 'text-green-500' :
                          apt.whatsapp_confirmation ? 'text-red-400' :
                          apt.client_phone ? 'text-gray-300' : 'text-gray-200'
                        }`} />
                      </span>
                      {/* WA Recordatorio */}
                      <span title={apt.whatsapp_reminder?.status === 'sent' || apt.reminder_sent ? 'Recordatorio enviado' : 'Recordatorio pendiente'}>
                        <Bell className={`w-4 h-4 ${
                          apt.whatsapp_reminder?.status === 'sent' || apt.reminder_sent ? 'text-blue-500' :
                          'text-gray-300'
                        }`} />
                      </span>
                      {/* Email */}
                      <span title={apt.email_confirmation?.status === 'sent' ? 'Email enviado' : 'Email pendiente'}>
                        <Mail className={`w-4 h-4 ${
                          apt.email_confirmation?.status === 'sent' ? 'text-emerald-500' :
                          apt.client_email ? 'text-gray-300' : 'text-gray-200'
                        }`} />
                      </span>
                      {/* WhatsApp directo */}
                      {apt.client_phone && (
                        <button
                          onClick={() => handleWhatsAppClick(apt)}
                          className="ml-1 p-1 rounded-full bg-green-500 hover:bg-green-600 transition-colors"
                          title="Enviar WhatsApp"
                        >
                          <MessageCircle className="w-3 h-3 text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Layout principal: Sidebar + Timeline */}
      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar con mini calendario - visible solo en desktop */}
        <aside className="hidden lg:block w-72 flex-shrink-0 border-r border-gray-200/50 bg-white/60 sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto">
          <div className="p-4">
            {/* Mini calendario inline */}
            <MiniCalendarSlide
              isOpen={true}
              onClose={() => {}}
              selectedDate={selectedDate}
              onDateSelect={(date: Date) => setSelectedDate(date)}
              appointmentDates={appointmentDates}
              workingDays={workingDays}
              blockedDates={blockedDates}
              inline={true}
            />

            {/* Resumen del día */}
            {(() => {
              const sd = selectedDate.toISOString().split('T')[0];
              const dayApts = appointments.filter(a => a.appointment_date.split('T')[0] === sd && a.status !== 'cancelled');
              const confirmed = dayApts.filter(a => a.status === 'confirmed').length;
              const pending = dayApts.filter(a => a.status === 'pending').length;
              return (
                <div className="mt-4 px-1">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100/50">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Resumen del día</h3>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Turnos</span>
                        <span className="font-bold text-gray-900">{dayApts.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Confirmados</span>
                        <span className="font-semibold text-emerald-600">{confirmed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Pendientes</span>
                        <span className="font-semibold text-amber-600">{pending}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Recordatorios - Estado de notificaciones */}
            {(() => {
              const sd = selectedDate.toISOString().split('T')[0];
              const dayApts = appointments.filter(a => a.appointment_date.split('T')[0] === sd && a.status !== 'cancelled');
              if (dayApts.length === 0) return null;

              const withWaConfirm = dayApts.filter(a => a.whatsapp_confirmation?.status === 'sent').length;
              const withWaReminder = dayApts.filter(a => a.whatsapp_reminder?.status === 'sent' || a.reminder_sent).length;
              const withEmailConfirm = dayApts.filter(a => a.email_confirmation?.status === 'sent').length;

              return (
                <div className="mt-3 px-1">
                  <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Bell className="w-3.5 h-3.5" />
                      Recordatorios
                    </h3>
                    <div className="space-y-2">
                      {/* WA Confirmación */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                          <span className="text-xs text-gray-600">WA Confirm.</span>
                        </div>
                        <span className={`text-xs font-bold ${withWaConfirm === dayApts.length ? 'text-green-600' : withWaConfirm > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {withWaConfirm}/{dayApts.length}
                        </span>
                      </div>
                      {/* WA Recordatorio */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Bell className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs text-gray-600">WA Recorda.</span>
                        </div>
                        <span className={`text-xs font-bold ${withWaReminder === dayApts.length ? 'text-green-600' : withWaReminder > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {withWaReminder}/{dayApts.length}
                        </span>
                      </div>
                      {/* Email */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-xs text-gray-600">Email Conf.</span>
                        </div>
                        <span className={`text-xs font-bold ${withEmailConfirm === dayApts.length ? 'text-green-600' : withEmailConfirm > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {withEmailConfirm}/{dayApts.length}
                        </span>
                      </div>

                      {/* Barra de progreso visual */}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                              style={{ width: `${dayApts.length > 0 ? (withWaConfirm / dayApts.length) * 100 : 0}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-semibold text-gray-500">
                            {dayApts.length > 0 ? Math.round((withWaConfirm / dayApts.length) * 100) : 0}%
                          </span>
                        </div>
                      </div>

                      {/* Lista de pacientes con status */}
                      <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5 max-h-40 overflow-y-auto">
                        {dayApts.slice(0, 8).map(apt => (
                          <div key={apt.id} className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-700 truncate flex-1 mr-2">
                              {(apt.client_display_name || apt.client_name || '').split(' ')[0]}
                            </span>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {apt.whatsapp_confirmation?.status === 'sent'
                                ? <CheckCircle className="w-3 h-3 text-green-500" />
                                : apt.client_phone
                                  ? <Clock className="w-3 h-3 text-gray-300" />
                                  : <span className="w-3 h-3" />
                              }
                              {(apt.whatsapp_reminder?.status === 'sent' || apt.reminder_sent)
                                ? <Bell className="w-3 h-3 text-blue-500" />
                                : <Bell className="w-3 h-3 text-gray-200" />
                              }
                            </div>
                          </div>
                        ))}
                        {dayApts.length > 8 && (
                          <p className="text-[10px] text-gray-400 text-center">+{dayApts.length - 8} más</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </aside>

        {/* Contenido principal - Timeline */}
        <main className="flex-1 min-w-0 px-4 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Cargando agenda...</p>
              </div>
            </div>
          ) : (
            <DayTimelineView
              selectedDate={selectedDate}
              appointments={searchTerm ? searchFilteredAppointments : appointments}
              workSchedule={daySchedule}
              onAppointmentClick={handleAppointmentClick}
              onWhatsAppClick={handleWhatsAppClick}
              professionalName={professionalName}
              blockedHours={blockedHours}
              onBlockHours={handleBlockHours}
              onUnblockHours={handleUnblockHours}
              specialDay={specialDay}
              diaCerrado={diaCerrado}
              onOpenSpecialDay={handleOpenSpecialDay}
              onRemoveSpecialDay={handleRemoveSpecialDay}
              sedes={sedes}
              onAddAppointment={handleAddAppointment}
              maxOverridesPerSlot={maxOverridesPerSlot}
              services={agendaServices}
              onQuickAdd={handleQuickAdd}
            />
          )}
        </main>
      </div>

      {/* Mini Calendario Slide - solo para mobile */}
      <div className="lg:hidden">
        <MiniCalendarSlide
          isOpen={showMiniCalendar}
          onClose={() => setShowMiniCalendar(false)}
          selectedDate={selectedDate}
          onDateSelect={(date: Date) => {
            setSelectedDate(date);
            setShowMiniCalendar(false);
          }}
          appointmentDates={appointmentDates}
          workingDays={workingDays}
          blockedDates={blockedDates}
        />
      </div>

      {/* Detalle del turno: el mismo componente que en el celular (DetalleTurno) */}
      {showDetailModal && selectedAppointment && (
        <DetalleTurno
          turno={selectedAppointment}
          servicio={getServiceDisplay(selectedAppointment)}
          precio={formatPrice(selectedAppointment.price)}
          onCerrar={() => setShowDetailModal(false)}
          onWhatsApp={() => handleWhatsAppClick(selectedAppointment)}
          notaDelTurno={
            <AppointmentNoteEditor
              appointmentId={selectedAppointment.id}
              value={selectedAppointment.notes}
              onSaved={(newNotes) => {
                setSelectedAppointment(prev => prev ? { ...prev, notes: newNotes } : prev);
                onRefresh();
              }}
            />
          }
          notificaciones={<NotificationIndicators appointment={selectedAppointment} compact={false} />}
          notasPrivadas={
            editingNotes ? (
              <div className="space-y-2">
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                  rows={3}
                  placeholder="Solo las ve el profesional"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setEditingNotes(false); setNotesValue(selectedAppointment.professional_notes || ''); }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    disabled={actionLoading}
                    className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                  >
                    {actionLoading ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setEditingNotes(true)}
                className="w-full rounded-2xl bg-slate-50 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-slate-100"
                title="Editar"
              >
                {selectedAppointment.professional_notes || <span className="text-gray-400">Sin notas · tocá para escribir</span>}
              </button>
            )
          }
          tipo={user?.professional_type === 'belleza' ? 'belleza' : 'salud'}
          clinica={{ historia: mostrarBotonReceta, receta: mostrarBotonReceta, puedeEmitir: puedeEmitirRecetas }}
          acciones={{
            cargando: actionLoading,
            onConfirmar: () => handleConfirmWithTransferCheck(),
            onCompletar: () => handleAction('completed'),
            onReagendar: () => { setShowDetailModal(false); setShowRescheduleModal(true); },
            onCancelar: () => {
              const reason = prompt('Motivo de cancelación:');
              if (reason) handleAction('cancelled', { cancellationReason: reason });
            },
            onBorrar: () => setConfirmHideOpen(true),
            onSobreturno: maxOverridesPerSlot > 0 ? () => {
              const date = selectedAppointment.appointment_date.split('T')[0];
              const time = selectedAppointment.appointment_time.substring(0, 5);
              setShowDetailModal(false);
              setSelectedAppointment(null);
              handleAddAppointment(date, time);
            } : undefined,
          }}
        />
      )}

      {/* Confirmación de borrado (soft-hide reversible) */}
      <ConfirmModal
        open={confirmHideOpen}
        title="Borrar turno cancelado"
        message="Se quitará de la agenda este turno cancelado. Queda guardado por si necesitás recuperarlo."
        confirmLabel="Borrar"
        onConfirm={handleHideAppointment}
        onClose={() => setConfirmHideOpen(false)}
      />

      {/* Modal de verificación de transferencia */}
      {/* Modal de reagendamiento (reuso del modal del paciente) */}
      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={() => { setShowRescheduleModal(false); setSelectedAppointment(null); }}
        appointment={selectedAppointment ? {
          id: selectedAppointment.id,
          professional_id: selectedAppointment.professional_id,
          service_name: selectedAppointment.service_name,
          appointment_date: selectedAppointment.appointment_date?.split('T')[0] || selectedAppointment.appointment_date,
          appointment_time: selectedAppointment.appointment_time,
          duration: selectedAppointment.duration || 30,
          price: selectedAppointment.price || 0,
          client_name: (selectedAppointment as any).client_display_name || selectedAppointment.client_name,
          status: selectedAppointment.status,
          location_id: selectedAppointment.location_id,
        } : null}
        onSuccess={() => { setShowRescheduleModal(false); setSelectedAppointment(null); window.location.reload(); }}
        skipValidation={true}
      />

      {/* Modal de transferencia renderizado con Portal para estar por encima de todo */}
      {showTransferModal && pendingTransferAppointment && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.6)', padding: '1rem' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
              <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Turno con Transferencia
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Este turno de <span className="font-semibold">{pendingTransferAppointment.client_display_name}</span> fue agendado con pago por <span className="font-semibold text-amber-700">transferencia bancaria</span>.
              </p>
              <p className="text-gray-600 text-sm mb-6">
                ¿Deseas ir al verificador de transferencias para confirmar el pago antes de confirmar el turno?
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setPendingTransferAppointment(null);
                    setShowDetailModal(false);
                    setSelectedAppointment(null);
                    navigate('/professional/payment-verification');
                  }}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4" /> Sí, verificar transferencia</span>
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    const apt = pendingTransferAppointment;
                    setPendingTransferAppointment(null);
                    handleAction('confirmed');
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  Confirmar sin verificar pago
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setPendingTransferAppointment(null);
                  }}
                  className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CalendarAgendaView;